import { TableColumn } from '@backstage/core-components';
import { Chip, IconButton, Tooltip } from '@material-ui/core';
import PauseCircleOutlineIcon from '@material-ui/icons/PauseCircleOutline';
import { CallerTeamsState, SecurityIssue } from '../api/types';
import { canManageQuiesce } from '../utils/quiesceOwnership';

const centered = { headerStyle: { textAlign: 'center' as const } };

/** A reason is "expiring soon" once its expiry is within this many days —
 * mirrors the report page's own badge threshold (see QuiescedFindingsPage). */
const EXPIRING_SOON_DAYS = 7;

function isExpiringSoon(expiresAt: string | undefined): boolean {
  if (!expiresAt) return false;
  const msRemaining = new Date(expiresAt).getTime() - Date.now();
  return msRemaining > 0 && msRemaining <= EXPIRING_SOON_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Trailing "Actions" column shared by SingleViewPage's and
 * LicenseCompliancePage's issue/detail tables: a Quiesce button for
 * un-suppressed findings the caller's teams own, or a "Quiesced" chip
 * (with category / reason / quiesced-by / expiry tooltip, and an
 * "expiring soon" visual state) for findings already suppressed. The
 * Quiesce button is hidden entirely (not disabled) when `callerTeams` has
 * resolved and doesn't include the row's team, or when the backend reports
 * the quiesce service isn't configured — a `null` `callerTeams` fails open,
 * since the existing 403-on-submit flow is the real authorization gate.
 */
export function buildActionsColumn(
  onQuiesce: (row: SecurityIssue) => void,
  callerTeams: CallerTeamsState,
): TableColumn<SecurityIssue> {
  return {
    title: 'Actions',
    field: 'reason_id',
    sorting: false,
    ...centered,
    render: (row: SecurityIssue) => {
      if (row.reason_id) {
        const expiringSoon = isExpiringSoon(row.quiesce_expires_at);
        return (
          <Tooltip
            title={
              <>
                {row.category && <div>Category: {row.category}</div>}
                {row.quiesce_reason && <div>Reason: {row.quiesce_reason}</div>}
                {row.quiesced_by && <div>By: {row.quiesced_by}</div>}
                {row.quiesce_expires_at && (
                  <div>Expires: {row.quiesce_expires_at}</div>
                )}
              </>
            }
          >
            <Chip
              size="small"
              icon={<PauseCircleOutlineIcon fontSize="small" />}
              label={expiringSoon ? 'Quiesced (expiring soon)' : 'Quiesced'}
              variant="outlined"
              color={expiringSoon ? 'secondary' : 'default'}
            />
          </Tooltip>
        );
      }
      if (!canManageQuiesce(row.team, callerTeams)) {
        return null;
      }
      return (
        <Tooltip title="Quiesce this finding">
          <IconButton
            size="small"
            aria-label="Quiesce this finding"
            onClick={() => onQuiesce(row)}
          >
            <PauseCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    },
  };
}
